import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    Building2, ChevronLeft, Edit3, Calendar, Users, 
    Grid3X3, Image, ChevronRight, MapPin, Gift, CreditCard, Megaphone, Crown, ShoppingBag, Receipt, ExternalLink
} from 'lucide-react';
import { clubsService } from '../../../services';
import { useUser } from '../../../UserContext';
import './ManageClubs.css';
import VenueSubnav from './VenueSubnav';
import VenueAccessBanner from './VenueAccessBanner';
import { getClubPublicPath, getClubStorefrontPath } from '../../../utils/clubPublicPaths';

const ClubDashboard = () => {
    const { clubId } = useParams();
    const navigate = useNavigate();
    const { user } = useUser();
    const [club, setClub] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const subscriptionStatus = club?.subscription?.status || 'Free';
    const profileTier = club?.profileTier || 'Free';
    const isPremiumVenue = Boolean(club?.isPremiumVenue);
    const isPlatformAdmin = Boolean(user?.isAdmin);

    const fetchClubDetails = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await clubsService.getClubDashboard(clubId);
            setClub(data);
        } catch (err) {
            console.error(err);
            setError('Failed to load venue details. Please try again later.');
        } finally {
            setIsLoading(false);
        }
    }, [clubId]);

    useEffect(() => {
        fetchClubDetails();
    }, [fetchClubDetails]);

    const managementCards = [
        {
            title: 'Events',
            description: 'Create and manage competitions, bookings, and sessions',
            icon: Calendar,
            color: 'orange',
            action: () => navigate(`/dashboard/club/${clubId}/events`)
        },
        {
            title: 'Members',
            description: 'View and manage venue members and their permissions',
            icon: Users,
            color: 'blue',
            action: () => navigate(`/dashboard/club/${clubId}/members`)
        },
        {
            title: 'Stands',
            description: 'Configure shooting stands, pegs, and lanes',
            icon: Grid3X3,
            color: 'green',
            action: () => navigate(`/dashboard/club/${clubId}/stands`)
        },
        {
            title: 'Maps & Photos',
            description: 'Manage venue maps, photos, and gallery images',
            icon: Image,
            color: 'purple',
            isPremiumFeature: true,
            action: () => navigate(`/dashboard/club/${clubId}/media`)
        },
        {
            title: 'Offers & Perks',
            description: 'Plan venue offers and member-facing promotions inside Shoot Connect',
            icon: Gift,
            color: 'orange',
            action: () => navigate(`/dashboard/club/${clubId}/offers`)
        },
        {
            title: 'Marketplace / Shopfront',
            description: 'Manage the public venue shopfront, listings, perks, and visibility',
            icon: ShoppingBag,
            color: 'purple',
            isPremiumFeature: true,
            action: () => navigate(`/dashboard/club/${clubId}/storefront`)
        },
        {
            title: 'Till / POS',
            description: 'Take venue counter payments with product catalogue and sales ledger',
            icon: Receipt,
            color: 'blue',
            isPremiumFeature: true,
            action: () => navigate(`/dashboard/club/${clubId}/till`)
        },
        {
            title: 'Communications',
            description: 'Create targeted updates for members, admins, and owners',
            icon: Megaphone,
            color: 'blue',
            isPremiumFeature: true,
            action: () => navigate(`/dashboard/club/${clubId}/communications`)
        },
        {
            title: 'Venue Plan',
            description: 'Compare Free vs Premium venue features and request an upgrade',
            icon: CreditCard,
            color: 'green',
            action: () => navigate(`/dashboard/club/${clubId}/plan`)
        }
    ];

    if (isLoading) {
        return (
            <div className="manage-clubs-page">
                <div className="manage-clubs-loading">
                    <div className="manage-clubs-spinner"></div>
                    <p>Loading venue details...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="manage-clubs-page">
                <div className="manage-clubs-error">{error}</div>
            </div>
        );
    }

    if (!club) {
        return (
            <div className="manage-clubs-page">
                <div className="manage-clubs-empty">
                    <div className="manage-clubs-empty-icon">
                        <Building2 size={48} />
                    </div>
                    <h3>Venue Not Found</h3>
                    <p>The venue you're looking for doesn't exist or you don't have access.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="manage-clubs-page">
            {/* Back Button */}
            <button 
                className="club-dashboard-back"
                onClick={() => navigate('/dashboard/manage-clubs')}
            >
                <ChevronLeft size={18} />
                Back to All Venues
            </button>

            {/* Header */}
            <div className="club-dashboard-header">
                <div className="club-dashboard-header-icon">
                    <Building2 size={32} />
                </div>
                <div className="club-dashboard-header-info">
                    <h1>{club.name}</h1>
                    {club.address && (
                        <p className="club-dashboard-address">
                            <MapPin size={16} />
                            {club.address}
                        </p>
                    )}
                </div>
                <button 
                    className="club-dashboard-edit-btn"
                    onClick={() => navigate(`/dashboard/club/${clubId}/edit`)}
                >
                    <Edit3 size={18} />
                    Edit Details
                </button>
            </div>

            <VenueSubnav clubId={clubId} />

            <VenueAccessBanner club={club} clubId={clubId} navigate={navigate} />

            <div className="dashboard-card-container" style={{ marginBottom: '24px' }}>
                <div className="dashboard-card">
                    <div className="club-view-card-info" style={{ marginBottom: '10px' }}>
                        {isPremiumVenue ? <Crown size={18} className='club-view-card-info-icon' /> : <CreditCard size={18} className='club-view-card-info-icon' />}
                        <span>
                            Venue subscription: <strong>{subscriptionStatus}</strong> | Public profile tier: <strong>{profileTier}</strong>.
                            {' '}
                            {isPremiumVenue
                                ? 'Shoot Connect admin has enabled premium venue features.'
                                : 'Premium venue features are currently disabled until Shoot Connect admin enables them.'}
                        </span>
                    </div>
                    <div className="club-dashboard-quick-actions">
                        <a
                            className="manage-section-add-btn"
                            href={getClubPublicPath(club)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'none' }}
                        >
                            <ExternalLink size={16} />
                            View Public Venue Profile
                        </a>
                        <a
                            className="manage-section-add-btn"
                            href={`/dashboard/clubs/${clubId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'none' }}
                        >
                            <ExternalLink size={16} />
                            View Logged In Venue Profile
                        </a>
                        <a
                            className="manage-section-add-btn"
                            href={getClubStorefrontPath(club)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'none' }}
                        >
                            <ExternalLink size={16} />
                            View Venue Shopfront
                        </a>
                        <button
                            className="manage-section-add-btn"
                            onClick={() => navigate(`/dashboard/club/${clubId}/storefront`)}
                        >
                            <ShoppingBag size={16} />
                            Open Marketplace / Shopfront Manager
                        </button>
                        <button
                            className="manage-section-add-btn"
                            onClick={() => navigate(`/dashboard/club/${clubId}/plan`)}
                        >
                            <CreditCard size={16} />
                            Open Venue Plan
                        </button>
                        {isPlatformAdmin ? (
                            <button
                                className="manage-section-add-btn manage-section-add-btn-secondary"
                                onClick={() => navigate(`/dashboard/club/${clubId}/plan#shoot-connect-crm`)}
                            >
                                <Megaphone size={16} />
                                Open Shoot Connect CRM
                            </button>
                        ) : null}
                    </div>
                </div>

            </div>

            {/* Management Cards */}
            <div className="club-dashboard-grid">
                {managementCards.map((card, index) => {
                    const CardIcon = card.icon;
                    return (
                        <div 
                            key={index} 
                            className={`club-dashboard-card card-${card.color}`}
                            onClick={card.action}
                        >
                            <div className={`club-dashboard-card-icon icon-${card.color}`}>
                                <CardIcon size={24} />
                            </div>
                            <div className="club-dashboard-card-content">
                                <div className="venue-card-label-row">
                                    <h3>{card.title}</h3>
                                    {card.isPremiumFeature ? (
                                        <span className="venue-card-premium-badge">
                                            <Crown size={12} />
                                            Premium
                                        </span>
                                    ) : null}
                                </div>
                                <p>{card.description}</p>
                                {card.isPremiumFeature ? (
                                    <p style={{ marginTop: '10px', color: '#9a3412', fontWeight: 600 }}>
                                        Unlocks with Premium venue access
                                    </p>
                                ) : null}
                            </div>
                            <div className="club-dashboard-card-arrow">
                                <ChevronRight size={20} />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ClubDashboard;
